import { placeCallouts, type CalloutPlacement } from "sketch/callout-stack";
import type { PageBox } from "sketch/geometry";
import type { CalloutBinding, CalloutShape } from "sketch/shapes/callout";
import {
  createComputedCache,
  Vec,
  type Editor,
  type TLPage,
  type TLShapeId,
} from "tldraw";
import { invariant } from "ts-invariant";

import {
  annotationScale,
  calloutBlock,
  calloutPlacement,
  calloutSide,
  drawingBounds,
} from "@/sketch/annotation-scale";
import { boundAnchorPage } from "@/sketch/bound-point";
import {
  SHEET_ASCENT,
  SHEET_DESCENT,
  SHEET_LEADING,
  SHEET_MASK_PAD,
  SHEET_SIZE,
  type SheetLine,
} from "@/sketch/lettering";

/**
 * Callout layout: where the leader runs and where the lettering sits. Like
 * the dim, a callout is annotation, so its graphics are screen-constant:
 * every metric below is a screen pixel multiplied by k, the sheet's
 * annotation scale (`annotation-scale.ts`), and a callout reads the same on
 * a 1 1/2" detail as on a floorplan.
 */
/** The landing (shoulder) the lettering sits on. */
const LANDING = 14;

/** What the width handle can drag a note's wrap between, screen pixels. */
export const MIN_CALLOUT_WIDTH = 60;
export const MAX_CALLOUT_WIDTH = 800;

/** A callout has one leader, so it has one binding. */
export function calloutBindingOf(
  editor: Editor,
  calloutId: TLShapeId,
): CalloutBinding | undefined {
  return editor.getBindingsFromShape<CalloutBinding>(calloutId, "callout")[0];
}

/** Where the leader lands in page space: its bound target, or the free
 *  point carried by the shape's own transform. */
export function calloutTargetPage(editor: Editor, shape: CalloutShape): Vec {
  const binding = calloutBindingOf(editor, shape.id);
  const bound = binding && boundAnchorPage(editor, binding);
  if (bound) return bound;
  return Vec.From(
    editor.getShapePageTransform(shape.id).applyToPoint(shape.props.target),
  );
}

/** Every sheet-placed head on the page: one layout per page, cached the way
 *  any computed over the store is. Notes stand clear of the dims too. */
const sheetCache = createComputedCache(
  "sheet callouts",
  (editor: Editor, _page: TLPage) => {
    const callouts: CalloutPlacement[] = [];
    const drawing: PageBox[] = drawingBounds(editor);
    for (const other of editor.getCurrentPageShapes()) {
      if (other.type === "callout") {
        const callout = other as CalloutShape;
        callouts.push(
          calloutPlacement(
            callout,
            calloutBlock(editor, callout),
            calloutTargetPage(editor, callout),
          ),
        );
      }
      if (other.type !== "dim") continue;
      const bounds = editor.getShapePageBounds(other.id);
      if (bounds) {
        drawing.push({ x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h });
      }
    }
    return placeCallouts(callouts, drawing, annotationScale(editor));
  },
);

function sheetCallouts(editor: Editor) {
  const sheet = sheetCache.get(editor, editor.getCurrentPageId());
  invariant(sheet, "the current page exists");
  return sheet;
}

/** A line of the note, placed: where its glyphs start, the baseline they sit
 *  on, and how far they run. All local to the shape. */
export interface CalloutLine {
  text: string;
  x: number;
  baseline: number;
  w: number;
}

export interface CalloutLayout {
  /** Annotation scale: page units per screen pixel. */
  k: number;
  target: Vec;
  head: Vec;
  /** The polyline the renderer strokes, target end first. */
  leader: Vec[];
  /** Unit vector the leader arrives along, which the terminator points. */
  aim: Vec;
  lines: CalloutLine[];
  /** The note's block: where it wraps, what you can grab, and what the
   *  editor covers. A tag's block is the bubble's bounds. */
  box: { x: number; y: number; w: number; h: number };
  /** Which way the lettering runs from the head: 1 right, -1 left. */
  side: 1 | -1;
  /** Tag bubble radius; 0 when the head is plain lettering. */
  radius: number;
}

/** Lay out the leader and the head in shape-local space. */
export function calloutLayout(
  editor: Editor,
  shape: CalloutShape,
): CalloutLayout {
  const k = annotationScale(editor);
  const transform = editor.getShapePageTransform(shape.id).clone().invert();
  const target = Vec.From(
    transform.applyToPoint(calloutTargetPage(editor, shape)),
  );
  const placed = shape.props.head ?? sheetCallouts(editor).get(shape.id);
  invariant(placed, `callout ${shape.id} is not on the current page`);
  const head = Vec.From(placed);
  const block = calloutBlock(editor, shape);
  const blockH = block.height * k;
  const aimFrom = (from: Vec) => {
    const span = Vec.Sub(target, from);
    return span.len() < 1e-6 ? new Vec(-1, 0) : span.uni();
  };
  /** Glyphs sit on a baseline an ascent below their line's top. */
  const place = (top: number, x: (line: SheetLine) => number): CalloutLine[] =>
    block.lines.map((line) => ({
      text: line.text,
      x: x(line),
      baseline: top + (line.y + SHEET_ASCENT * SHEET_SIZE) * k,
      w: line.w * k,
    }));

  if (shape.props.tag !== "none") {
    const radius = block.radius * k;
    const aim = aimFrom(head);
    return {
      k,
      target,
      head,
      leader: [target, Vec.Add(head, aim.clone().mul(radius))],
      aim,
      lines: place(head.y - blockH / 2, (line) => head.x - (line.w * k) / 2),
      box: {
        x: head.x - radius,
        y: head.y - radius,
        w: radius * 2,
        h: radius * 2,
      },
      side: 1,
      radius,
    };
  }

  // The landing meets the middle of the first line and the block hangs off
  // it, which is where a leader note attaches on a sheet (AutoCAD calls it
  // middle-of-top-line, and it is the default there). It also means typing
  // adds lines downward instead of shoving the note up off its own leader.
  const side = calloutSide(head, target);
  const elbow = new Vec(head.x - side * LANDING * k, head.y);
  const boxW = shape.props.width * k;
  const top = head.y - (SHEET_LEADING * k) / 2;
  const left = side === 1 ? head.x : head.x - boxW;
  return {
    k,
    target,
    head,
    leader: [target, elbow, head],
    aim: aimFrom(elbow),
    lines: place(top, (line) =>
      side === 1 ? left + line.x * k : left + boxW - line.w * k,
    ),
    box: { x: left, y: top, w: boxW, h: blockH },
    side,
    radius: 0,
  };
}

/** Where the width handle sits: the edge the lettering wraps at. */
export function widthHandlePoint(layout: CalloutLayout): Vec {
  const { box, side } = layout;
  return new Vec(side === 1 ? box.x + box.w : box.x, box.y + box.h / 2);
}

/** What the lettering masks out, so the grid (and stray linework) does not
 *  read through a note. A sheet calls this a background mask. It hugs the
 *  glyphs, not the block: the empty half of a wide note is not something to
 *  hide the drawing behind. */
export function letteringMask(layout: CalloutLayout) {
  const { lines, k } = layout;
  const first = lines[0];
  const last = lines[lines.length - 1];
  if (!first || !last) return { x: 0, y: 0, w: 0, h: 0 };
  const pad = SHEET_MASK_PAD * k;
  const left = Math.min(...lines.map((line) => line.x));
  const right = Math.max(...lines.map((line) => line.x + line.w));
  return {
    x: left - pad,
    y: first.baseline - SHEET_ASCENT * SHEET_SIZE * k - pad,
    w: right - left + pad * 2,
    h:
      last.baseline -
      first.baseline +
      (SHEET_ASCENT + SHEET_DESCENT) * SHEET_SIZE * k +
      pad * 2,
  };
}

/** The tag bubble's outline, local: a circle is drawn as one, a hex gets
 *  the flat-topped six points a keynote tag is drawn with. */
export function tagPoints(center: Vec, radius: number): Vec[] {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i;
    return new Vec(
      center.x + radius * Math.cos(angle),
      center.y + radius * Math.sin(angle),
    );
  });
}
